// Support-csapat form tiszta segédei (nem-komponens): form-értékek, alapértékek, a @atlas/contracts write-sémája
// (a resolverhez) és a lokalizált zod-hibatérkép. A minta az org-structure/form.ts (kétnyelvű név/leírás) +
// employees (tag-multiselect). A validálás forrás-igazsága a contract (NEM saját séma). Külön fájl a komponenstől
// (react-refresh tisztaság).
import { SupportTeamWriteBodySchema } from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { type TFunction } from 'i18next';
import { type Resolver } from 'react-hook-form';
import { type z } from 'zod';

/** A form mező-értékei (string-alapú; a wire-alakra a `toSupportTeamBody` normalizál: üres → null). */
export interface SupportTeamFormValues {
  nameHu: string;
  nameEn: string;
  descriptionHu: string;
  descriptionEn: string;
  email: string;
  /** A KÍVÁNT tag-halmaz (belső identity-id-k); a szerver diffeli a meglévővel (add/remove). */
  memberIds: string[];
  /** A KÍVÁNT szolgáltatás-halmaz (billing/services id-k); a szerver diffeli (supportTeamId beállítás/null). */
  serviceIds: string[];
}

export const EMPTY_SUPPORT_TEAM_FORM: SupportTeamFormValues = {
  nameHu: '',
  nameEn: '',
  descriptionHu: '',
  descriptionEn: '',
  email: '',
  memberIds: [],
  serviceIds: [],
};

type SupportTeamWriteBody = z.infer<typeof SupportTeamWriteBodySchema>;

/** Form-értékek → wire-body: üres leírás → null; a NÉV és az E-MAIL kötelező (trimmelve); a tag-halmaz átmegy. */
export function toSupportTeamBody(v: SupportTeamFormValues): SupportTeamWriteBody {
  const nn = (s: string): string | null => (s.trim() === '' ? null : s.trim());
  return {
    nameHu: v.nameHu.trim(),
    nameEn: v.nameEn.trim(),
    descriptionHu: nn(v.descriptionHu),
    descriptionEn: nn(v.descriptionEn),
    email: v.email.trim(),
    memberIds: v.memberIds,
    serviceIds: v.serviceIds,
  };
}

/** Lokalizált zod-hibatérkép: mező (`issue.path[0]`) + kód → a `validation:supportTeams.*` réteg. */
export function buildSupportTeamErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    if (field === 'nameHu' || field === 'nameEn') {
      return { message: t('validation:supportTeams.name') };
    }
    if (field === 'email') {
      return { message: t('validation:supportTeams.email') };
    }
    if (typeof field === 'string' && field in EMPTY_SUPPORT_TEAM_FORM) {
      return { message: t('validation:supportTeams.generic') };
    }
    return { message: ctx.defaultError };
  };
}

/** RHF-resolver a contract-sémából: a validálás a WIRE-alakon fut (a `toSupportTeamBody` normalizálása után),
 *  a hiba-pathok 1:1 a form-mezők. Sikerkor az EREDETI form-értékek mennek tovább (a body-t a mutáció építi). */
export function buildSupportTeamResolver(t: TFunction): Resolver<SupportTeamFormValues> {
  const base = zodResolver(SupportTeamWriteBodySchema, {
    errorMap: buildSupportTeamErrorMap(t),
  }) as unknown as Resolver<SupportTeamFormValues>;
  return async (values, context, options) => {
    const result = await base(
      toSupportTeamBody(values) as unknown as SupportTeamFormValues,
      context,
      options,
    );
    if (Object.keys(result.errors).length > 0) return { values: {}, errors: result.errors };
    return { values, errors: {} };
  };
}
